import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  Pagination,
  Stack,
  Alert,
} from '@mui/material';

export interface Transaction {
  id: string;
  transaction_date: string;
  amount: number;
  type: 'income' | 'expense';
  account_type: 'general' | 'reserve';
  category: string;
  description?: string | null;
  reference_number?: string | null;
}

export interface TransactionDetailTableProps {
  transactions: Transaction[];
  title?: string;
  subtitle?: string;
  totalAmount?: number;
  pageSize?: number;
  showAccountType?: boolean;
}

type Order = 'asc' | 'desc';
type OrderBy = 'transaction_date' | 'category' | 'description' | 'amount';

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
  }).format(value);
};

export const TransactionDetailTable: React.FC<TransactionDetailTableProps> = ({
  transactions,
  title,
  subtitle,
  totalAmount,
  pageSize = 10,
  showAccountType = false,
}) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('transaction_date');

  // ソート処理
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // データのソート
  const sortedTransactions = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];
      
      // null/undefined の処理
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      // 文字列の場合は空文字列をデフォルトに
      if (orderBy === 'category' || orderBy === 'description') {
        aValue = aValue || '';
        bValue = bValue || '';
      }
      
      if (order === 'desc') {
        return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    });
    return sorted;
  }, [transactions, order, orderBy]);

  // ページネーション
  const totalPages = Math.ceil(sortedTransactions.length / pageSize);
  const paginatedTransactions = sortedTransactions.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const getTransactionColor = (type: 'income' | 'expense') => {
    return type === 'income' ? 'success' : 'error';
  };

  if (transactions.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        {title && (
          <Typography variant="h6" gutterBottom>
            {title}
            {subtitle && ` - ${subtitle}`}
            {totalAmount !== undefined && ` （合計: ${formatCurrency(totalAmount)}）`}
          </Typography>
        )}
        <Alert severity="info">
          表示する取引データがありません
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
          {subtitle && ` - ${subtitle}`}
          {totalAmount !== undefined && ` （合計: ${formatCurrency(totalAmount)}）`}
        </Typography>
      )}
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'transaction_date'}
                  direction={orderBy === 'transaction_date' ? order : 'asc'}
                  onClick={() => handleRequestSort('transaction_date')}
                >
                  日付
                </TableSortLabel>
              </TableCell>
              {showAccountType && (
                <TableCell>会計</TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'category'}
                  direction={orderBy === 'category' ? order : 'asc'}
                  onClick={() => handleRequestSort('category')}
                >
                  カテゴリ
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'description'}
                  direction={orderBy === 'description' ? order : 'asc'}
                  onClick={() => handleRequestSort('description')}
                >
                  摘要
                </TableSortLabel>
              </TableCell>
              <TableCell>メモ</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'amount'}
                  direction={orderBy === 'amount' ? order : 'asc'}
                  onClick={() => handleRequestSort('amount')}
                >
                  金額
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{transaction.transaction_date}</TableCell>
                {showAccountType && (
                  <TableCell>
                    <Chip 
                      label={transaction.account_type === 'general' ? '一般' : '積立'} 
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Chip 
                    label={transaction.category} 
                    size="small"
                    color={getTransactionColor(transaction.type)} 
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={transaction.description || ''}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {transaction.description || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={transaction.reference_number || ''}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                      {transaction.reference_number || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    color={getTransactionColor(transaction.type)}
                    fontWeight={transaction.type === 'income' ? 'bold' : 'normal'}
                  >
                    {formatCurrency(transaction.amount)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {totalPages > 1 && (
        <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Pagination 
            count={totalPages}
            page={page}
            onChange={(_event, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}
    </Paper>
  );
};